import type { Option } from "@tento-lona/sheets";
import "@tento-ui/components/editable-text";

import { Tracing } from "@tento-core/tracing";
import { html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { Divider } from "@tento-ui/components/divider";
import { EditableText } from "@tento-ui/components/editable-text";
import { Overlay } from "@tento-ui/components/overlay";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { LonaModalWebComponent } from "@tento-ui/ui/modal-component";
import { LonaModalHeader } from "@tento-ui/ui/modal-header-styles";
import type { RowsClient } from "@tento-lona";
import { type RowId, RowKey } from "@tento-lona/sheets";

@component({
  name: "lona-lookup-key-modal",
})
export class LookupKeyModal extends LonaModalWebComponent {
  onSave?: (lookupKey: Option<string>) => Promise<Option<{ error: string }>>;
  #rows: Option<RowsClient> = null;
  #rowId: Option<RowId> = null;
  #$input: EditableText = this.hydrate("lookup-key-input");
  #$saveButton = this.hydrate("save-button");
  #$cancelButton = this.hydrate("cancel-button");
  #$errorMessage = this.hydrate("error-message");
  #$availabilityStatus = this.hydrate("availability-status");
  #checkTimer: Option<ReturnType<typeof setTimeout>> = null;
  #availabilityState: "idle" | "checking" | "available" | "unavailable" =
    "idle";

  static $styles: Style.Compat[] = [
    LonaModalWebComponent.$style,
    LonaModalHeader.$style,
    Divider.$hr,
    Button.$style,
    css`
      :host {
        --std-modal-max-width: 440px;
        --std-modal-min-height: 0px;
        --std-modal-max-height: 340px;
        background: var(--bg);
        border-radius: 12px;
      }

      #root {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .lona-modal-header {
        padding: 20px 20px;
      }

      hr {
        margin: 0;
      }

      #actions {
        position: relative;
        padding: 12px 20px;
        gap: 12px;
        justify-content: flex-end;
        border-top: 1px solid var(--divider);
      }

      #content {
        position: relative;
        margin: 20px;
      }

      #lookup-key-input {
        width: 100%;
        background: var(--bg-surface-light);
        border-radius: 8px;
        padding: 12px;
      }

      #description {
        color: var(--text-sec);
      }

      #title {
        --margin-bottom: 8px
      }

      #error-message {
        color: var(--red);
        display: none;
        flex-grow: 1;
        align-self: center;
      }

      #error-message.visible {
        display: block;
      }

      #availability-status {
        display: none;
        flex-grow: 1;
        align-self: center;
        margin-left: 4px;
      }

      #availability-status.checking {
        display: block;
        color: var(--text-sec);
      }

      #availability-status.available {
        display: block;
        color: var(--green);
      }

      #availability-status.unavailable {
        display: block;
        color: var(--red);
      }
    `,
  ];

  static $html = html`
    <std-col id="root">
      <std-col class="lona-modal-header">
        <h1 id=title class=h4>Edit Lookup Key</h1>
        <p id="description">Change the lookup key used to reference this row in formulas and integrations</p>
      </std-col>
      <hr />
      <std-col id="content">
        <std-editable-text id="lookup-key-input" placeholder=":my-key"></std-editable-text>
      </std-col>
      <std-row id="actions">
        <p id="availability-status" class="small"></p>
        <p id="error-message" class="small"></p>
        <button id="cancel-button">Cancel</button>
        <button id="save-button" primary>Save</button>
      </std-row>
    </std-col>
  `;

  connectedCallback() {
    super.connectedCallback();
    this.#$input.enableEditing(true);

    GESTURE_MANAGER.addPointerEvent(this.#$saveButton, {
      onClick: () => this.#handleSave(),
    });

    GESTURE_MANAGER.addPointerEvent(this.#$cancelButton, {
      onClick: () => this.#handleCancel(),
    });

    this.#$input.addEventListener("input", () =>
      this.#scheduleAvailabilityCheck(),
    );

    // Focus the input
    setTimeout(() => {
      this.#$input.focus();
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#checkTimer != null) clearTimeout(this.#checkTimer);
    GESTURE_MANAGER.unregister(this.#$saveButton);
    GESTURE_MANAGER.unregister(this.#$cancelButton);
  }

  bind(
    rows: RowsClient,
    currentLookupKey: Option<string>,
    rowId: Option<RowId>,
    rowLabel: Option<string>,
    onSave: (lookupKey: Option<string>) => Promise<Option<{ error: string }>>,
  ): this {
    this.#rows = rows;
    this.#rowId = rowId;

    // Generate suggested key from label if no current key exists
    let suggestedKey = currentLookupKey;
    if (!currentLookupKey && rowLabel) {
      suggestedKey = this.#generateLookupKeyFromLabel(rowLabel);
    }

    this.#$input.bind(suggestedKey, ":my-key");
    this.onSave = onSave;
    return this;
  }

  #generateLookupKeyFromLabel(label: string): string {
    const sanitized = label
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-_]/g, "")
      .replace(/^-+|-+$/g, "")
      .replace(/-+/g, "-");

    if (!sanitized) {
      return ":my-key";
    }

    return ":" + sanitized;
  }

  #normalizeKey(value: string): string {
    if (value && !value.startsWith(":")) {
      value = ":" + value;
    }
    if (value) {
      value = value.toLowerCase();
    }
    return value;
  }

  #scheduleAvailabilityCheck(): void {
    if (this.#checkTimer != null) clearTimeout(this.#checkTimer);
    const value = this.#normalizeKey(this.#$input.controller.text ?? "");
    if (!value || value.length < 2) {
      this.#setAvailability("idle");
      return;
    }
    this.#setAvailability("checking");
    this.#checkTimer = setTimeout(() => this.#checkAvailability(value), 350);
  }

  async #checkAvailability(key: string): Promise<void> {
    if (!this.#rows) return;
    try {
      const data = await this.#rows.checkLookupKeyExists(RowKey.of(key), this.#rowId);
      Tracing.log("lookup-key-availability", {
        key,
        available: data.available,
        conflictingRowLabel: data.conflictingRowLabel ?? null,
      });
      this.#setAvailability(
        data.available ? "available" : "unavailable",
        data.conflictingRowLabel ?? undefined,
      );
    } catch {
      this.#setAvailability("idle");
    }
  }

  #setAvailability(
    state: "idle" | "checking" | "available" | "unavailable",
    label?: string,
  ): void {
    this.#availabilityState = state;
    this.#$availabilityStatus.className =
      `small ${state === "idle" ? "" : state}`.trim();

    if (state === "checking") {
      this.#$availabilityStatus.textContent = "Checking...";
    } else if (state === "available") {
      this.#$availabilityStatus.textContent = "✓ Available";
    } else if (state === "unavailable") {
      this.#$availabilityStatus.textContent = label
        ? `✗ Already in use by "${label}"`
        : "✗ Already in use";
    } else {
      this.#$availabilityStatus.textContent = "";
    }

    const blocked = state === "checking" || state === "unavailable";
    this.#$saveButton.toggleAttribute("disabled", blocked);
  }

  async #handleSave(): Promise<void> {
    if (this.#availabilityState === "unavailable") {
      this.#showError("This lookup key is already in use");
      return;
    }

    let value = this.#normalizeKey(this.#$input.controller.text ?? "");

    // Validate allowed characters (after the colon: a-z, 0-9, -, _, %, :)
    if (value && value.length > 1) {
      const keyPart = value.substring(1);
      const validPattern = /^[a-z0-9\-_%:]+$/;
      if (!validPattern.test(keyPart)) {
        this.#showError(
          "Lookup key can only contain lowercase letters, numbers, hyphens, underscores, colons, and percent signs",
        );
        return;
      }
    }

    // Clear any previous error
    this.#clearError();

    // Call the save callback
    const result = await this.onSave?.(value);

    if (result?.error) {
      this.#showError(result.error);
    } else {
      this.#setAvailability("idle");
      Overlay.dismiss(this);
    }
  }

  #showError(message: string): void {
    this.#$errorMessage.textContent = message;
    this.#$errorMessage.classList.add("visible");
  }

  #clearError(): void {
    this.#$errorMessage.textContent = "";
    this.#$errorMessage.classList.remove("visible");
  }

  #handleCancel(): void {
    Overlay.dismiss(this);
  }

  static makeWith(
    rows: RowsClient,
    currentLookupKey: Option<string>,
    rowId: Option<RowId>,
    rowLabel: Option<string>,
    onSave: (lookupKey: Option<string>) => Promise<Option<{ error: string }>>,
  ): LookupKeyModal {
    const modal = LookupKeyModal.make();
    modal.bind(rows, currentLookupKey, rowId, rowLabel, onSave);
    return modal;
  }
}
